<template>
  <!-- ★ 根节点：v-show 控制显示，永远存在于 DOM，绝不挂 v-if -->
  <div class="import-modal" v-show="visible" @click.self="handleClose">
    <div class="import-panel">
      <!-- 顶部 -->
      <div class="import-header">
        <span class="import-title">学习资料导入</span>
        <span class="import-close" @click="handleClose">✕</span>
      </div>

      <!-- 主体：左右两框 -->
      <div class="import-body">
        <!-- 左框：资料输入 -->
        <div class="left-box">
          <div class="box-title title-bar">
            <span>资料输入</span>
            <button
              class="clear-btn"
              :disabled="parsing || converting"
              @click="handleClearInput"
              title="清空资料输入"
            >清空</button>
          </div>
          <div v-if="parsing" class="parse-status">⏳ 正在解析文件…</div>
          <div v-else-if="parseError" class="parse-status error">⚠ {{ parseError }}</div>

          <!-- 上传区 -->
          <div class="upload-area"
               @dragover.prevent
               @drop.prevent="handleDrop"
               @click="$refs.fileInput.click()">
            <input ref="fileInput" type="file" hidden
                   accept=".txt,.md,.doc,.docx,.pdf,.xlsx,.xls,.html,.htm"
                   @change="handleFileChange" />
            <p v-if="!fileName">📄 点击或拖拽上传文档<br>
              <span class="tip">支持 txt / md / doc / docx / pdf（非扫描件） / xls / xlsx / csv / html</span>
            <p v-else class="file-name">📎 {{ fileName }}（点击可替换）</p>
          </div>

          <div class="or-line"><span>或直接粘贴资料</span></div>

          <!-- 粘贴输入：解析结果也会填到这里，可直接编辑 -->
          <textarea class="paste-area" v-model="pasteText"
                    placeholder="在此粘贴学习资料文本…" />
        </div>

        <!-- 中间转化按钮 -->
        <div class="convert-column">
          <button v-if="converting" class="stop-btn" @click="handleStopConvert">■ 停止</button>
          <button class="convert-btn" :disabled="converting || parsing" @click="handleConvert">
            <span v-if="!converting">转化</span>
            <span v-else>转化中…</span>
            <span class="arrow" :class="{ spin: converting }">➜</span>
          </button>
          <div v-if="converting" class="converting-tip">正在生成知识图谱…</div>
        </div>

        <!-- 右框：知识地图展示 -->
        <div class="right-box">
          <div class="box-title">知识地图</div>

          <div v-if="!genUrl" class="empty-tip">
            点击「转化」后，左侧资料将生成知识图谱并在此展示
          </div>

          <div v-else class="graph-wrap">
            <!-- ★ iframe 命名，供 form target 指向 -->
            <iframe
              name="kgFrame"
              ref="kgFrame"
              class="kg-iframe"
              @load="onFrameLoad"
            ></iframe>

            <div class="use-btn-bar">
              <button class="use-btn" :disabled="!kgDone" @click="handleUseGraph">
                {{ kgDone ? '使用该图谱' : (kgFailed ? '生成失败' : '生成中，请稍候…') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { rawPost } from '@/utils/request';
// ★ 前端文件解析：txt / md / docx / pdf 统一输出纯文本
import { parseFile } from '@/utils/fileParser';

export default {
  name: 'ImportMaterialDialog',
  props: {
    visible: Boolean,
    // 当前学段，转化接口可能需要（h/m/p）
    level: { type: String, default: '' },
  },
  data() {
    return {
      fileName: '',
      fileRaw: null,        // File 对象
      parsedMaterial: '',   // ★ 前端解析出的文本
      parseError: '',       // ★ 解析失败提示
      parsing: false,       // ★ 解析中状态
      pasteText: '',
      converting: false,
      genUrl: '',     // ★ iframe 地址（/dh/kg/gen?...）
      kgTask: null,   // ★ { taskId, graphName, jsonUrl }
      kgDone: false,  // ★ 生成是否完成
      kgFailed: false,// ★ 生成是否失败
    };
  },
  watch: {
    visible(v) {
      if (v) {
      }
    },
  },
  // ★ 根节点永远渲染（真实 div，非注释占位符），mounted 时搬到 body，
  // 之后 Vue 的所有 patch（包括 v-show 的 display 切换）都发生在 body 下这份 DOM 上
  mounted() {
    if (this.$el && this.$el.parentNode !== document.body) {
      this._originalParent = this.$el.parentNode;
      document.body.appendChild(this.$el);
    }
      // ★ 接收 iframe 等待页的通知
    this._onKgMessage = (e) => {
      // 同源校验（等待页 postMessage 用的是 origin）
      if (e.origin !== window.location.origin) return;
      // ★ 用户已停止：忽略迟到的完成/失败通知
      if (this._kgAborted) return;
      const d = e.data || {};
      if (d.type === 'kg_completed') {
        this._kgAborted = false;     // 正常流程收到通知后清除标记
        this.kgDone = true;
        this.kgFailed = false;
        this.converting = false;
        if (!this.kgTask && d.taskId) {
          this.kgTask = { taskId: d.taskId, graphName: d.graphName, jsonUrl: d.jsonUrl, level: d.level || this.level || 'h' };
        }
        this.$message.success('知识图谱生成完成');
        // ★ 通知父组件：图谱已就绪（红点用）
        this.$emit('graph-ready');
      } else if (d.type === 'kg_failed') {
        this.kgFailed = true;
        this.kgDone = false;
        this.converting = false;
        this.$message.error('知识图谱生成失败：' + (d.error || '未知错误'));
      } else if (d.type === 'kg_redirect' && d.resultUrl) {
        // ★ iframe 内自动切换到结果页（右侧窗口直接展示图谱）
        this.genUrl = d.resultUrl;
      }
    };
    window.addEventListener('message', this._onKgMessage);
  },
  beforeDestroy() {
    // 先还原回原父节点，让 Vue 的销毁流程正常摘除 DOM，避免残留
    if (this._originalParent && this.$el && this.$el.parentNode === document.body) {
      this._originalParent.appendChild(this.$el);
    }
    this._originalParent = null;
    window.removeEventListener('message', this._onKgMessage);
    this._onKgMessage = null;
  },
  methods: {
    // 清空资料输入：文件、解析结果、粘贴文本一并复位
    handleClearInput() {
      if (this.converting) {
        this.$message.warning('转化进行中，无法清空');
        return;
      }
      if (!this.fileName && !(this.pasteText || '').trim()) {
        this.$message.info('暂无内容可清空');
        return;
      }
      this.$confirm('确定清空当前输入的资料？', '提示', {
        confirmButtonText: '清空',
        cancelButtonText: '取消',
        type: 'warning',
        zIndex: 100001,
      }).then(() => {
        this.fileRaw = null;
        this.fileName = '';
        this.parsedMaterial = '';
        this.pasteText = '';
        this.parseError = '';
        this.$message.success('已清空');
      }).catch(() => {});
    },
    // ★ 停止转化：复位状态 + 断开 iframe（后端继续跑也收不到通知，不会误报成功）
    handleStopConvert() {
      this.$confirm('确定停止本次转化？', '提示', {
        confirmButtonText: '停止',
        cancelButtonText: '继续转化',
        type: 'warning',
        zIndex: 100001,
      }).then(() => {
        this._kgAborted = true;      // ★ 终止标记：拦截迟到的完成通知
        this.converting = false;
        this.kgDone = false;
        this.kgFailed = false;
        this.kgTask = null;
        this.genUrl = '';            // ★ 销毁 iframe，断开与后端任务的关联
        this.$message.info('已停止转化');
      }).catch(() => {});
    },

    handleClose() {
      this.$emit('update:visible', false);
    },

    // ---------- 文件 ----------
    handleFileChange(e) {
      const f = e.target.files && e.target.files[0];
      if (f) this.acceptFile(f);
      // 允许连续选择同一个文件
      e.target.value = '';
    },

    handleDrop(e) {
      const f = e.dataTransfer.files && e.dataTransfer.files[0];
      if (f) this.acceptFile(f);
    },

    acceptFile(f) {
      const okTypes = /\.(txt|md|docx?|pdf|xlsx?|csv|html?)$/i;
      if (!okTypes.test(f.name)) {
        this.$message.warning('仅支持 txt / md / doc / docx / pdf / xls / xlsx / csv / html / htm');
        return;
      }
      this.fileRaw = f;
      this.fileName = f.name;
      this.parseFileInternal(f);
    },


    // ★ 统一解析入口
    async parseFileInternal(f) {
      this.parsing = true;
      this.parseError = '';
      try {
        const text = await parseFile(f);
        this.parsedMaterial = text;
        // 填进粘贴框方便预览/编辑（txt/md/docx/pdf 都填）
        this.pasteText = text;
      } catch (err) {
        this.parseError = err.message || '文件解析失败';
        this.parsedMaterial = '';
        this.pasteText = '';
        this.fileRaw = null;   // 解析失败的文件不再参与转化
      } finally {
        this.parsing = false;
      }
    },

    hasMaterial() {
      return !!(this.pasteText || '').trim();
    },

    // ---------- 转化：form POST 到 iframe（大文本不受 URL 限制） ----------
    handleConvert() {
      const material = (this.pasteText || '').trim();
      if (!material) {
        this.$message.warning('请先上传文档或粘贴资料');
        return;
      }
      if (this.converting) return;
      this._kgAborted = false;       // ★ 新一轮转化，恢复接收通知
      // ★ 清空时机在这里：点击「转化」= 明确开始新一轮
      this.genUrl = '';
      this.kgTask = null;
      this.kgDone = false;
      this.kgFailed = false;
      const query = `${material}。根据上面的背景材料生成知识图谱。`;

      this.converting = true;
      this.kgDone = false;
      this.kgFailed = false;
      this.kgTask = null;

      // genUrl 置为非空以显示 iframe 容器（不作为 src 使用）
      this.genUrl = 'about:blank';

      this.$nextTick(() => {
        // 动态创建 form，target 指向 iframe，POST 的响应直接渲染在 iframe 内
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = '/dh/kg/gen';
        form.target = 'kgFrame';   // ★ 关键：响应落在 iframe 里

        const addField = (name, value) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.value = value;
          form.appendChild(input);
        };
        addField('level', this.level || 'm');
        addField('query', query);   // ★ 整篇资料放 POST body，无 414 问题

        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
      });
    },


    // iframe 首次加载完成：同源读取任务信息（postMessage 的兜底）
    onFrameLoad() {
      try {
        const t = this.$refs.kgFrame?.contentWindow?.KG_TASK;
        if (t && t.taskId && !this.kgTask) {
          this.kgTask = { taskId: t.taskId, graphName: t.graphName, level: t.level };
        }
      } catch (e) { /* 跨域/时序问题则忽略，等 postMessage */ }
    },

    // ---------- 使用图谱 ----------
    async handleUseGraph() {
      if (!this.kgDone || !this.kgTask) return;
      const { taskId, graphName, level } = this.kgTask;
      try {
        const { data } = await rawPost('/dh/kg/save', {
          task_id: taskId,
          level: level || this.level || 'h',   // ★ 优先用后端返回的 level
          name: graphName || this.fileName || '导入图谱',
        });
        if (!data || !data.success || !data.kgid) {
          throw new Error(data?.error || '保存失败');
        }
        this.$message.success('图谱已保存到我的图谱');
        this.$emit('use-graph', { name: data.name, kgid: data.kgid, level: data.level || level });
        this.$emit('graph-consumed');
        this.handleClose();
      } catch (err) {
        console.error('[ImportMaterialDialog] 图谱保存失败:', err);
        this.$message.error('图谱保存失败：' + (err.message || '请稍后重试'));
      }
    },

  },
};
</script>

<style lang="scss" scoped>
.parse-status {
  padding: 6px 14px;
  font-size: 12px;
  color: #e6a23c;
  background: #fdf6ec;
  &.error {
    color: #f56c6c;
    background: #fef0f0;
  }
}

.import-modal {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 99999 !important;
  background: rgba(0,0,0,.45);
  display: flex;
  align-items: center;
  justify-content: center;
}

.import-panel {
  width: min(1024px, 100vw);
  height: min(780px, 100vh);
  background: #fff;
  border-radius: 18px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 25px 45px -12px rgba(0,0,0,.3);
}

.import-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid #f0f0f0;

  .import-title {
    font-size: 17px;
    font-weight: 600;
    color: rgb(118,6,245);
  }
  .import-close {
    cursor: pointer;
    color: #999;
    font-size: 18px;
    &:hover { color: #f56c6c; }
  }
}

.import-body {
  flex: 1;
  display: flex;
  gap: 12px;
  padding: 16px;
  min-height: 0;
}

.left-box {
  .title-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    .clear-btn {
      padding: 4px 14px;
      border: none;
      border-radius: 20px;
      background: linear-gradient(135deg, rgba(82,93,252,.12), rgba(222,96,253,.12));
      color: #525dfc;
      font-size: 12px;
      cursor: pointer;
      transition: all .2s;

      &:hover:not(:disabled) {
        background: linear-gradient(135deg, rgba(82,93,252,.22), rgba(222,96,253,.22));
      }

      &:active:not(:disabled) { transform: scale(.95); }

      &:disabled { opacity: .5; cursor: not-allowed; }
    }
  }
}

.left-box, .right-box {
  flex: 1;
  min-width: 0;
  border: 1px solid #e5e9f5;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fafbff;

  .box-title {
    padding: 10px 14px;
    font-weight: 600;
    font-size: 14px;
    color: #556;
    border-bottom: 1px solid #eef1fa;
    background: #f5f7ff;
    flex-shrink: 0;
  }
}

.upload-area {
  margin: 12px 14px 0;
  padding: 22px 12px;
  text-align: center;
  border: 1.5px dashed #c3cdf0;
  border-radius: 10px;
  cursor: pointer;
  color: #7a86b8;
  font-size: 14px;
  line-height: 1.7;
  transition: all .2s;

  &:hover {
    border-color: #525dfc;
    background: #f3f5ff;
  }

  .tip { font-size: 12px; color: #aab; }
  .file-name { color: #409eff; }
}

.or-line {
  text-align: center;
  color: #bbb;
  font-size: 12px;
  margin: 10px 0 0;

  span {
    background: #fafbff;
    padding: 0 8px;
  }
}

.paste-area {
  flex: 1;
  margin: 8px 14px 14px;
  padding: 10px;
  border: 1px solid #e0e6f5;
  border-radius: 10px;
  resize: none;
  font-size: 13px;
  line-height: 1.6;
  outline: none;
  &:focus {
    border-color: #525dfc;
    box-shadow: 0 0 0 3px rgba(82,93,252,.1);
  }
}

.convert-column {
  width: 96px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  .stop-btn {
    order: 0;               // 停止在上（DOM 顺序已是第一，此行可省，写明意图）
    padding: 7px 14px;
    border: 1px solid #f5c6c0;
    border-radius: 20px;
    cursor: pointer;
    color: #c45655;
    font-size: 13px;
    background: #fdf1f0;
    white-space: nowrap;
    transition: all .2s;
    &:hover { background: #fbe4e1; border-color: #e8948a; }
    &:active { transform: scale(.95); }
  }

  .convert-btn {
    order: 1;   
    padding: 10px 14px;
    border: none;
    border-radius: 24px;
    cursor: pointer;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    background: linear-gradient(90deg, rgb(82,93,252), rgb(222,96,253));
    box-shadow: 0 4px 16px rgba(46,91,255,.3);
    display: flex;
    align-items: center;
    gap: 6px;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(46,91,255,.4);
    }

    &:disabled {
      opacity: .6;
      cursor: not-allowed;
    }

    .arrow {
      transition: transform .3s;
      &.spin {
        animation: rot 1s linear infinite;
        display: inline-block;
      }
    }
  }

  .converting-tip {
    font-size: 12px;
    color: #e6a23c;
    text-align: center;
  }
}

@keyframes rot {
  to { transform: rotate(360deg); }
}

.right-box {
  .kg-iframe {
    flex: 1;
    width: 100%;
    border: none;
    min-height: 0;
    background: #fff;
  }

  .empty-tip {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #aab;
    font-size: 13px;
    padding: 0 20px;
    text-align: center;
    line-height: 1.8;
  }

  .graph-wrap {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  .graph-name {
    padding: 8px 14px;
    font-weight: 600;
    font-size: 13px;
    color: #409eff;
    border-bottom: 1px dashed #e5e9f5;
    flex-shrink: 0;
  }

  .graph-view {
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }
  .use-btn-bar {
    display: flex;                 // 让 button 成为 flex item
    justify-content: center;       // ★ 按钮在栏内水平居中
    flex-shrink: 0;
  }

  .use-btn {
    margin: 10px 0 14px;           // 去掉左右固定 margin，交给 flex 居中
    padding: 9px 28px;             // ★ 左右 padding 决定文字两侧留白
    border: none;
    border-radius: 22px;
    color: #fff;
    font-size: 14px;
    cursor: pointer;
    white-space: nowrap;           // ★ 文字不换行，宽度完全由内容决定
    background: linear-gradient(135deg, #20b2aa, #2e8b57);
    transition: all .2s;

    &:hover:not(:disabled) { filter: brightness(1.08); }
    &:active:not(:disabled) { transform: scale(.97); }

    &:disabled { opacity: .7; cursor: not-allowed; }
  }
}

@media (max-width: 767px) {
  .import-body {
    flex-direction: column;
    overflow-y: auto;
  }
  .convert-column {
    width: 100%;
    flex-direction: row;        // ★ 改为左右排列
    justify-content: center;
    align-items: center;
    gap: 10px;
    padding: 4px 0;

    .stop-btn {
      order: 0;                 // ★ 停止在左
    }
    .convert-btn {
      order: 1;                 // 转化在右
    }
    .converting-tip {
      order: 2;
      width: 100%;              // 提示文字换到下一行，占满
      text-align: center;
    }
  }
  .left-box, .right-box {
    min-height: 450px;
  }
  .kg-iframe { min-height: 260px; }
}
</style>
